import ServiceFactory from "../../service";

/*
 * Ad Campaigns shared bits (AD_CAMPAIGNS_EMAIL_AUTOMATION_UX.md Phase A).
 * API: /v1/campaign/ad/* on CampaignService (reads anonymous + x-app-id).
 *
 * WHITE-LABEL RULE (§2.6): the UI binds everything to the CHANNEL, never
 * the provider. Display and Retargeting are always provider-neutral;
 * YouTube renders as "Video". The raw generated name is technical
 * small-print only. Never render `provider` anywhere.
 *
 * LIST-FETCH REALITY (Phase A on today's API): the unfiltered list 504s at
 * the gateway (N+1 per-campaign stats — the at-scale endpoint ask stands),
 * but (type × status) slices complete. The dashboard fires the slices in
 * parallel and renders progressively. `range=DAY` aggregates the per-day
 * stat rows = LIFETIME totals (label it honestly); date-windowed metrics
 * arrive with the at-scale endpoint.
 */

export function adSvc(api) {
  const Service = ServiceFactory.getType(`/campaign/ad${api}`, "product"); // /v1 root
  return new Service();
}

// ---- Ranges (§213/§215) ------------------------------------------------
// Every campaign endpoint accepts ?range=<platform token> and ?from=&to=
// (probed live: list, /campaigns/{id}, /stats/campaign/{id}, /assets all
// window server-side). Custom tokens (custom_YYYYMMDD_YYYYMMDD) become
// from/to; presets pass through. Default = all_time (lifetime).
export const DEFAULT_RANGE = "all_time";
export function rangeQs(range) {
  const m = /^custom_(\d{4})(\d{2})(\d{2})_(\d{4})(\d{2})(\d{2})$/.exec(String(range || ""));
  if (m) return `from=${m[1]}-${m[2]}-${m[3]}&to=${m[4]}-${m[5]}-${m[6]}`;
  return `range=${encodeURIComponent(range || DEFAULT_RANGE)}`;
}

// Known type tokens (the generated-name grammar's channel segment) and
// their tenant-facing channel labels. NOTE: the managed-account grammar
// ({Account}_{Event}_{Tier?}_{Type}_{Location}) names YouTube campaigns with
// the token "TV" (FlightPlanner.ChannelNameToken) — it MUST be here or a
// flight's per-market YouTube campaigns don't split into (flight, market) and
// each market wrongly shows as its own flight row.
export const TYPE_TOKENS = ["Display", "Search", "Social", "YouTube", "TV", "Video", "Retarget", "Retargeting", "Performance", "CTV", "OTT"];
export const CHANNEL_LABELS = {
  Display: "Display",
  Search: "Search",
  Social: "Social",
  YouTube: "YouTube",
  TV: "YouTube",
  Video: "Social Video",
  Retarget: "Retargeting",
  Retargeting: "Retargeting",
  Performance: "Performance",
  CTV: "CTV/OTT",
  OTT: "CTV/OTT",
  "CTV/OTT": "CTV/OTT"
};
// The channel filter's full vocabulary (what the platform sells), in wizard order.
export const ALL_CHANNEL_LABELS = ["Display", "Retargeting", "Search", "Social", "Social Video", "YouTube", "CTV/OTT"];
// Status vocabulary in the data spans generations: "Enabled" AND "Active"
// both mean running.
export function isActiveStatus(s) {
  return /^(enabled|active)$/i.test(String(s || ""));
}
export function channelLabel(type) {
  return CHANNEL_LABELS[type] || type || "—";
}

// The dashboard's fetch slices — each completes where the unfiltered list
// 504s. Statuses beyond these two haven't been observed in the data.
// ENABLED SLICES FIRST (Track 2 mitigation): the default view is Active
// flights, and the paused sets are the bulk of the slow slices — active
// data renders first, paused fills in behind it.
export const FETCH_SLICES = [];
["Enabled", "Active", "Paused", "Removed"].forEach(st => {
  ["Display", "Search", "Social", "YouTube", "Retarget", "CTV/OTT"].forEach(t => FETCH_SLICES.push({
    type: t,
    status: st
  }));
});

// FAST PATH: with Service.AdCampaign 1.0.2 (batched stats query — staging
// 2026-08-01) the unfiltered list returns in <5s. Try it first; the slice
// strategy below is the fallback for environments still on 1.0.1.
export async function fetchAllCampaigns(adSvcFn, range) {
  const d = await adSvcFn(`/campaigns?${rangeQs(range)}`).getAsync();
  return d && d.campaigns || [];
}
export function readAllCache(tenantId, range) {
  try {
    const raw = sessionStorage.getItem(`plunk-camp-all:${tenantId}:${range || DEFAULT_RANGE}`);
    if (!raw) return null;
    const {
      t,
      rows
    } = JSON.parse(raw);
    if (Date.now() - t > 15 * 60 * 1000) return null;
    return rows;
  } catch (e) {
    return null;
  }
}
export function clearAllCache(tenantId) {
  try {
    for (let i = sessionStorage.length - 1; i >= 0; i--) {
      const k = sessionStorage.key(i);
      if (k && k.indexOf(`plunk-camp-all:${tenantId}`) === 0) sessionStorage.removeItem(k);
    }
  } catch (e) {/* best-effort */}
}
export function writeAllCache(tenantId, rows, range) {
  try {
    const slim = rows.map(r => ({
      id: String(r.id),
      name: r.name,
      type: r.type,
      status: r.status,
      budget: r.budget,
      metrics: r.metrics
    }));
    sessionStorage.setItem(`plunk-camp-all:${tenantId}:${range || DEFAULT_RANGE}`, JSON.stringify({
      t: Date.now(),
      rows: slim
    }));
  } catch (e) {/* best-effort */}
}

// Slice cache (Track 2): sessionStorage, 15-min TTL — honest against the
// ~4h stats-refresh cadence the page discloses. Rows are stored SLIM
// (only the fields the dashboard renders); the detail page fetches fresh.
const CACHE_TTL_MS = 15 * 60 * 1000;
export function sliceCacheKey(tenantId, slice, range) {
  return `plunk-camp-slice:${tenantId}:${slice.type}:${slice.status}:${range || DEFAULT_RANGE}`;
}
export function readSliceCache(tenantId, slice, range) {
  try {
    const raw = sessionStorage.getItem(sliceCacheKey(tenantId, slice, range));
    if (!raw) return null;
    const {
      t,
      rows
    } = JSON.parse(raw);
    if (Date.now() - t > CACHE_TTL_MS) return null;
    return rows;
  } catch (e) {
    return null;
  }
}
export function writeSliceCache(tenantId, slice, rows, range) {
  try {
    const slim = rows.map(r => ({
      id: String(r.id),
      name: r.name,
      type: r.type,
      status: r.status,
      budget: r.budget,
      metrics: r.metrics
    }));
    sessionStorage.setItem(sliceCacheKey(tenantId, slice, range), JSON.stringify({
      t: Date.now(),
      rows: slim
    }));
  } catch (e) {/* quota — cache is best-effort */}
}

// Decompose a generated campaign name: tokens up to the type segment are
// the FLIGHT (event + year), tokens after it are the MARKET.
// "The_Suite_Life_Sale_Event_2026_Display_Broward_County"
//   → { flight: "The Suite Life Sale Event 2026", market: "Broward County" }
// Some tenants' account token IS the tenant id ("a316487212239"), so it leads
// the generated campaign name and clutters the flight label. Drop a leading
// tenant-id-shaped token ("a" + a long digit run) — a real account name
// ("RobbStucky") never matches, so those are left intact.
const TENANT_TOKEN = /^a\d{10,}$/i;
function dropTenantToken(tokens) {
  return tokens.length > 1 && TENANT_TOKEN.test(tokens[0]) ? tokens.slice(1) : tokens;
}
export function parseName(name) {
  const tokens = String(name || "").split("_");
  const idx = tokens.findIndex(t => TYPE_TOKENS.includes(t));
  if (idx < 1) return {
    flight: dropTenantToken(tokens).join(" "),
    market: "",
    typeToken: null
  };
  return {
    flight: dropTenantToken(tokens.slice(0, idx)).join(" "),
    market: tokens.slice(idx + 1).join(" "),
    typeToken: tokens[idx]
  };
}
export function num(v) {
  return (Number(v) || 0).toLocaleString("en-US");
}
export function usd(v) {
  const n = Number(v) || 0;
  return "$" + n.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
}
export function pct(v) {
  return `${(Number(v) || 0).toFixed(2)}%`;
}
// Budget amounts are stored in each provider's minor unit: Google micros
// (6000000 = $6.00/day), Facebook CENTS (600 = $6.00/day) — the same rule
// the flight planner and the worker use. An older Google writer generation
// stored small raw values (262 fleet rows carry amount=300, units
// unverifiable); those render no number rather than a wrong one. The
// provider only picks the unit — it is never shown (§2.6 white-label).
export function budgetDollars(budget, provider) {
  if (!budget || !budget.amount) return null;
  const n = Number(budget.amount);
  if (!(n > 0)) return null;
  // Facebook: cents, except older console-built rows, which hold micros (whole multiples of 10,000).
  if (String(provider || "").toLowerCase() === "facebook") return n >= 1000000 && n % 10000 === 0 ? n / 1e6 : n / 100;
  return n >= 100000 ? n / 1e6 : null;
}
export function budgetUsd(budget, provider) {
  const d = budgetDollars(budget, provider);
  return d == null ? null : usd(d);
}

// ---- Phase B writes ----------------------------------------------------
// Both endpoints enqueue to the campaign worker's SQS queue and return
// {queued:true} — the provider sync is ASYNC. The UI applies the change
// optimistically and says "queued"; stats/status reconcile on the worker's
// next pass. [Authorize] — the service pipeline attaches the bearer.
export function setCampaignStatus(id, status) {
  return adSvc(`/campaigns/${id}/status?status=${encodeURIComponent(status)}`).putAsync({});
}
// dailyBudgetMicros is MICROS for every campaign; the service converts to
// the provider's unit (Facebook cents) before it reaches the worker.
export function setCampaignBudget(id, dailyBudgetMicros) {
  return adSvc(`/campaigns/${id}/budget?dailyBudget=${encodeURIComponent(String(dailyBudgetMicros))}`).putAsync({});
}
// Rename a campaign (the tenant's own label — local write, no platform push).
export function setCampaignName(id, name) {
  return adSvc(`/campaigns/${id}/name?name=${encodeURIComponent(name)}`).putAsync({});
}
// Remove a campaign: the worker deletes it AT THE PROVIDER (Google REMOVED /
// Meta DELETED) then marks it Removed locally. Async + irreversible — confirm
// in the UI before calling. deleteAsync with no arg hits the base path.
export function removeCampaign(id) {
  return adSvc(`/campaigns/${id}`).deleteAsync();
}
// Patch a campaign row across the sessionStorage caches so the dashboard
// reflects an optimistic write without a refetch.
export function patchCampaignCaches(tenantId, id, patch) {
  try {
    const keys = [];
    for (let i = 0; i < sessionStorage.length; i++) {
      const k = sessionStorage.key(i);
      if (k.startsWith(`plunk-camp-all:${tenantId}`) || k.startsWith(`plunk-camp-slice:${tenantId}:`)) keys.push(k);
    }
    keys.forEach(k => {
      const raw = sessionStorage.getItem(k);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      let hit = false;
      parsed.rows = (parsed.rows || []).map(r => {
        if (String(r.id) !== String(id)) return r;
        hit = true;
        return {
          ...r,
          ...patch
        };
      });
      if (hit) sessionStorage.setItem(k, JSON.stringify(parsed));
    });
  } catch (e) {/* cache is best-effort */}
}
export function aggregate(rows) {
  const t = {
    impressions: 0,
    clicks: 0,
    spend: 0,
    conversions: 0,
    videoViews: 0,
    revenue: 0
  };
  rows.forEach(r => {
    const m = r.metrics || {};
    t.impressions += Number(m.impressions) || 0;
    t.clicks += Number(m.clicks) || 0;
    t.spend += Number(m.spend) || 0;
    t.conversions += Number(m.conversions) || 0;
    t.videoViews += Number(m.videoViews) || 0;
    t.revenue += Number(m.revenue) || 0; // §94 attributed overlay (0 when flag off)
  });
  t.ctr = t.impressions > 0 ? t.clicks / t.impressions * 100 : 0;
  t.cpc = t.clicks > 0 ? t.spend / t.clicks : 0;
  t.cpm = t.impressions > 0 ? t.spend / t.impressions * 1000 : 0;
  t.roas = t.spend > 0 && t.revenue > 0 ? t.revenue / t.spend : 0;
  return t;
}